<template>
  <div class="multiselect-wrapper" v-click-outside="closeDropdown">
    <div class="form-group" :class="{ 'has-error': validated }">
      <!-- Label -->
      <label v-if="label" class="control-label">
        <span v-if="required" class="text-danger">*</span> {{ label }}
      </label>

      <!-- Selected Items Display -->
      <div
          class="multiselect-input"
          @click="toggleDropdown"
          :class="{ 'is-open': isOpen, 'is-focused': isOpen }"
      >
        <div class="selected-tags" v-if="selectedItems.length > 0">
          <span
              v-for="item in selectedItems"
              :key="item[valueKey]"
              class="tag"
          >
            {{ item[textKey] }}
            <span class="tag-remove" @click.stop="removeItem(item)">×</span>
          </span>
        </div>
        <!-- The search field opens the dropdown but never toggles it: the click
             also reaches .multiselect-input, and letting both handlers run
             would open and close the list on a single click. -->
        <input
            type="text"
            v-model="searchQuery"
            :placeholder="selectedItems.length === 0 ? placeholder : ''"
            class="multiselect-search"
            @click.stop="openDropdown"
            @input="openDropdown"
        />
        <span class="dropdown-arrow">▼</span>
      </div>

      <!-- Dropdown List -->
      <div v-if="isOpen" class="multiselect-dropdown">
        <div
            v-for="item in filteredItems"
            :key="item[valueKey]"
            class="multiselect-option"
            :class="{ 'is-selected': isSelected(item) }"
            @click="toggleItem(item)"
        >
          <input
              type="checkbox"
              :checked="isSelected(item)"
              class="option-checkbox"
          />
          <span class="option-text">{{ item[textKey] }}</span>
        </div>
        <div v-if="filteredItems.length === 0" class="no-results">
          No se encontraron resultados
        </div>
      </div>

      <!-- Error Message -->
      <span v-if="validated" class="invalid-feedback">
        <slot name="textError">Este campo es requerido</slot>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "MultiSelect",
  props: {
    label: {
      type: String,
      default: ""
    },
    value: {
      type: Array,
      default: () => []
    },
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    textKey: {
      type: String,
      default: "name"
    },
    valueKey: {
      type: String,
      default: "id"
    },
    placeholder: {
      type: String,
      default: "Seleccionar..."
    },
    required: {
      type: Boolean,
      default: false
    },
    validated: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      internalValue: []
    };
  },
  computed: {
    selectedItems() {
      if (!Array.isArray(this.internalValue)) return [];

      return this.items.filter(item =>
          this.internalValue.includes(item[this.valueKey])
      );
    },
    filteredItems() {
      if (!this.searchQuery) return this.items;

      const query = this.searchQuery.toLowerCase();
      return this.items.filter(item =>
          item[this.textKey].toLowerCase().includes(query)
      );
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        // Asegurar que siempre sea un array
        this.internalValue = Array.isArray(newValue) ? [...newValue] : [];
      }
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    openDropdown() {
      this.isOpen = true;
    },
    closeDropdown() {
      this.isOpen = false;
      this.searchQuery = "";
    },
    toggleItem(item) {
      const value = item[this.valueKey];
      const index = this.internalValue.indexOf(value);

      if (index > -1) {
        this.internalValue.splice(index, 1);
      } else {
        this.internalValue.push(value);
      }

      this.$emit("input", [...this.internalValue]);
      this.$emit("change", [...this.internalValue]);
    },
    removeItem(item) {
      const value = item[this.valueKey];
      const index = this.internalValue.indexOf(value);

      if (index > -1) {
        this.internalValue.splice(index, 1);
        this.$emit("input", [...this.internalValue]);
        this.$emit("change", [...this.internalValue]);
      }
    },
    isSelected(item) {
      return this.internalValue.includes(item[this.valueKey]);
    }
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style scoped>
.multiselect-wrapper {
  position: relative;
  width: 100%;
}

.form-group {
  margin-bottom: 1rem;
}

.control-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.text-danger {
  color: #dc3545;
  margin-right: 2px;
}

.multiselect-input {
  min-height: 38px;
  padding: 4px 5px 4px 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  display: grid;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  position: relative;
  transition: border-color 0.15s ease-in-out;
}

.multiselect-input:hover {
  border-color: #80bdff;
}

.multiselect-input.is-open,
.multiselect-input.is-focused {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  outline: 0;
}

.has-error .multiselect-input {
  border-color: #dc3545;
}

.has-error .multiselect-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
}

.tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background-color: #007bff;
  color: white;
  border-radius: 3px;
  font-size: 12px;
  gap: 4px;
}

.tag-remove {
  cursor: pointer;
  font-weight: bold;
  font-size: 18px;
  line-height: 1;
  margin-left: 4px;
}

.tag-remove:hover {
  color: #ffcccc;
}

.multiselect-search {
  border: none;
  outline: none;
  flex: 1;
  min-width: 100px;
  padding: 4px;
  font-size: 12px;
}

.dropdown-arrow {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #6c757d;
  font-size: 10px;
}

.multiselect-dropdown {
  display: contents;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.multiselect-option {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.15s;
}

.multiselect-option:hover {
  background-color: #f8f9fa;
}

.multiselect-option.is-selected {
  background-color: #e7f3ff;
}

.option-checkbox {
  cursor: pointer;
}

.option-text {
  flex: 1;
  user-select: none;
  font-size:12px;
}

.no-results {
  padding: 12px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.invalid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}
</style>